import React, { useState, useEffect, useRef, useCallback } from "react";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Bot, Send, Plus, Trash2, MessageSquare, Sparkles, TrendingUp,
  TrendingDown, Minus, AlertTriangle, Lightbulb, ChevronRight,
  Clock, Zap, Target, Users, BarChart3, CalendarDays, Megaphone,
  ClipboardList, Loader2, ArrowRight, Star, BookmarkPlus, X,
  Rocket, FileText, RefreshCw, Check, XCircle, ArrowUpRight,
  Gauge, Hammer, Eye, DollarSign,
} from "lucide-react";
import ActionDraftsPanel from "@/components/ActionDraftsPanel";
import MarketingPanel from "@/components/MarketingPanel";
import DevOpsPanel from "@/components/DevOpsPanel";
import ResearchPanel from "@/components/ResearchPanel";
import GrowthLabPanel from "@/components/GrowthLabPanel";
import ProductLabPanel from "@/components/ProductLabPanel";
import TikTokTrendsPanel from "@/components/TikTokTrendsPanel";
import FinancePanel from "@/components/FinancePanel";
import OperationsPanel from "@/components/OperationsPanel";

const ACTION_ICONS = {
  campaign: Megaphone,
  task: ClipboardList,
  zalo: MessageSquare,
  report: BarChart3,
  customer_list: Users,
  insight: Lightbulb,
};

export default function CopilotPage() {
  const [mainTab, setMainTab] = useState("chat");
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch conversations & suggestions
  useEffect(() => {
    api.get("/copilot/conversations").then(r => setConversations(r.data.conversations || [])).catch(() => {});
    api.get("/copilot/suggestions").then(r => setSuggestions(r.data.suggestions || [])).catch(() => {});
  }, []);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversation = useCallback(async (convId) => {
    try {
      const { data } = await api.get(`/copilot/conversations/${convId}`);
      setMessages(data.messages || []);
      setActiveConvId(convId);
    } catch { toast.error("Lỗi tải cuộc hội thoại"); }
  }, []);

  const startNewChat = () => {
    setActiveConvId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");

    // Optimistic add user message
    const userMsg = { role: "user", content: msg, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const { data } = await api.post("/copilot/chat", {
        message: msg,
        conversation_id: activeConvId,
      });

      // Set active conversation
      if (!activeConvId && data.conversation_id) {
        setActiveConvId(data.conversation_id);
        // Refresh sidebar
        api.get("/copilot/conversations").then(r => setConversations(r.data.conversations || [])).catch(() => {});
      }

      // Add AI response
      const aiMsg = {
        role: "assistant",
        content: data.answer,
        intent: data.intent,
        metrics: data.key_metrics || [],
        recommendations: data.recommendations || [],
        quick_actions: data.quick_actions || [],
        action_drafts: data.action_drafts || [],
        analysis_summary: data.analysis || {},
        trends: data.trends || {},
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi gửi tin nhắn");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (convId) => {
    try {
      await api.delete(`/copilot/conversations/${convId}`);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConvId === convId) startNewChat();
      toast.success("Đã xoá");
    } catch { toast.error("Lỗi xoá"); }
  };

  const handleQuickAction = async (action) => {
    sendMessage(action.label);
  };

  return (
    <div className="flex h-[calc(100vh-64px)] -m-4 md:-m-6" data-testid="copilot-page">
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="w-72 bg-[#0a0f1a] border-r border-[#1e293b] flex flex-col absolute md:relative z-30 h-full" data-testid="copilot-sidebar">
          {/* Tabs */}
          <div className="grid grid-cols-4 gap-0.5 p-1.5 border-b border-[#1e293b]">
            {[
              { key: "chat", label: "Chat", icon: MessageSquare },
              { key: "actions", label: "Actions", icon: ClipboardList },
              { key: "features", label: "Đề xuất", icon: Rocket },
              { key: "marketing", label: "Marketing", icon: Megaphone },
              { key: "devops", label: "DevOps", icon: Sparkles },
              { key: "research", label: "Research", icon: Target },
              { key: "growth", label: "Growth", icon: Zap },
              { key: "products", label: "Products", icon: Star },
              { key: "tiktok", label: "TikTok", icon: Eye },
              { key: "finance", label: "Tài chính", icon: DollarSign },
              { key: "operations", label: "COO", icon: Gauge },
            ].map(tab => (
              <button key={tab.key} onClick={() => setMainTab(tab.key)}
                className={`flex flex-col items-center gap-0.5 py-1.5 rounded-md text-[8px] font-medium transition-colors ${
                  mainTab === tab.key ? "bg-lime-500/15 text-lime-400" : "text-slate-500 hover:text-slate-300 hover:bg-[#1e293b]/50"
                }`}
                data-testid={`tab-${tab.key}`}>
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {mainTab === "chat" && (
            <>
              <div className="p-3 border-b border-[#1e293b]">
                <Button onClick={startNewChat}
                  className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-2 h-9 text-xs" data-testid="new-chat-btn">
                  <Plus className="w-4 h-4" /> Cuộc hội thoại mới
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {conversations.map(conv => (
                  <div key={conv.id}
                    onClick={() => loadConversation(conv.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer group transition-colors ${
                      activeConvId === conv.id ? "bg-[#1e293b] text-lime-400" : "text-slate-400 hover:bg-[#1e293b]/50 hover:text-slate-200"
                    }`} data-testid={`conv-${conv.id}`}>
                    <MessageSquare className="w-4 h-4 shrink-0" />
                    <span className="text-xs truncate flex-1">{conv.title}</span>
                    <button onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                      className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-red-400 p-0.5">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {conversations.length === 0 && (
                  <p className="text-xs text-slate-600 text-center mt-4">Chưa có cuộc hội thoại</p>
                )}
              </div>
            </>
          )}

          {mainTab === "features" && (
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-[10px] text-slate-600 text-center">Xem đề xuất tính năng bên phải</p>
            </div>
          )}

          {(mainTab === "actions" || mainTab === "marketing" || mainTab === "devops" || mainTab === "research" || mainTab === "growth" || mainTab === "products") && (
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-[10px] text-slate-600 text-center">
                {mainTab === "actions" ? "Quản lý Actions bên phải" : mainTab === "devops" ? "Technical Health bên phải" : mainTab === "research" ? "AI Research bên phải" : mainTab === "products" ? "AI Product Lab bên phải" : "Marketing AI Engine bên phải"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-[#0f172a]">
        {mainTab === "chat" && (
          <>
            <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-6">
              {messages.length === 0 ? (
                <WelcomeScreen suggestions={suggestions} onSend={sendMessage} />
              ) : (
                messages.map((msg, idx) => (
                  <ChatBubble key={idx} message={msg} onQuickAction={handleQuickAction} />
                ))
              )}
              {loading && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-lime-500/20 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-lime-400" />
                  </div>
                  <div className="bg-[#1e293b] border border-[#334155] rounded-2xl rounded-tl-none px-4 py-3">
                    <div className="flex items-center gap-2 text-slate-400 text-xs">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Đang phân tích dữ liệu...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="border-t border-[#1e293b] px-4 md:px-8 py-4 bg-[#0a0f1a]">
              <div className="flex items-center gap-2 max-w-4xl mx-auto">
                <button onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="text-slate-500 hover:text-slate-300 p-2" data-testid="toggle-sidebar">
                  <MessageSquare className="w-5 h-5" />
                </button>
                <div className="flex-1 flex items-center gap-2 bg-[#1e293b] border border-[#334155] rounded-xl px-4 py-2 focus-within:border-lime-500/50 transition-colors">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    placeholder="Hỏi Copilot bất kỳ điều gì về kinh doanh..."
                    className="border-0 bg-transparent text-slate-200 placeholder:text-slate-600 focus-visible:ring-0 text-sm flex-1 h-8"
                    disabled={loading}
                    data-testid="copilot-input"
                  />
                  <Button onClick={() => sendMessage()} disabled={loading || !input.trim()}
                    size="sm" className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] h-8 w-8 p-0 rounded-lg" data-testid="send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-center text-[10px] text-slate-600 mt-2">Copilot phân tích dữ liệu thực từ hệ thống KARINOX Arena</p>
            </div>
          </>
        )}
        {mainTab === "actions" && <ActionDraftsPanel />}
        {mainTab === "features" && <FeatureSuggestionsPanel />}
        {mainTab === "marketing" && <MarketingPanel />}
        {mainTab === "devops" && <DevOpsPanel />}
        {mainTab === "research" && <ResearchPanel />}
        {mainTab === "growth" && <GrowthLabPanel />}
        {mainTab === "products" && <ProductLabPanel />}
        {mainTab === "tiktok" && <TikTokTrendsPanel />}
        {mainTab === "finance" && <FinancePanel />}
        {mainTab === "operations" && <OperationsPanel />}
      </div>
    </div>
  );
}

// ==================== WELCOME SCREEN ====================
function WelcomeScreen({ suggestions, onSend }) {
  const [dailyInsight, setDailyInsight] = useState(null);

  useEffect(() => {
    api.get("/copilot/daily-insight").then(r => setDailyInsight(r.data)).catch(() => {});
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto" data-testid="welcome-screen">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-lime-500/20 to-emerald-500/20 border border-lime-500/30 flex items-center justify-center mb-4">
        <Sparkles className="w-8 h-8 text-lime-400" />
      </div>
      <h2 className="text-xl font-bold text-slate-200 mb-1">KARINOX Copilot</h2>
      <p className="text-sm text-slate-500 mb-6 text-center">
        AI đa tác nhân phân tích kinh doanh thực, đề xuất hành động, tạo chiến dịch
      </p>

      {/* Daily Insight Card */}
      {dailyInsight && dailyInsight.highlights && dailyInsight.highlights.length > 0 && (
        <div className="w-full bg-[#1e293b] border border-lime-500/20 rounded-xl p-4 mb-6" data-testid="daily-insight">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-lime-400" />
            <span className="text-xs font-semibold text-lime-400">Tóm tắt hôm nay</span>
          </div>
          <div className="space-y-1">
            {dailyInsight.highlights.map((h, i) => (
              <p key={i} className="text-xs text-slate-400">• {h}</p>
            ))}
          </div>
          <div className="flex gap-3 mt-3">
            {dailyInsight.utilization_pct !== undefined && (
              <div className="bg-[#0f172a] rounded-lg px-3 py-1.5">
                <p className="text-[9px] text-slate-600">Công suất</p>
                <p className="text-sm font-bold text-slate-200">{dailyInsight.utilization_pct}%</p>
              </div>
            )}
            {dailyInsight.yesterday_revenue > 0 && (
              <div className="bg-[#0f172a] rounded-lg px-3 py-1.5">
                <p className="text-[9px] text-slate-600">Hôm qua</p>
                <p className="text-sm font-bold text-lime-400">{(dailyInsight.yesterday_revenue / 1000).toFixed(0)}k</p>
              </div>
            )}
            {dailyInsight.churn_risk > 0 && (
              <div className="bg-[#0f172a] rounded-lg px-3 py-1.5">
                <p className="text-[9px] text-slate-600">Churn risk</p>
                <p className="text-sm font-bold text-amber-400">{dailyInsight.churn_risk}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
        {suggestions.map((s, i) => (
          <button key={i} onClick={() => onSend(s.text)}
            className="flex items-center gap-3 px-4 py-3 bg-[#1e293b] border border-[#334155] rounded-xl text-left hover:border-lime-500/30 hover:bg-[#1e293b]/80 transition-all group"
            data-testid={`suggestion-${i}`}>
            <Zap className="w-4 h-4 text-lime-400 shrink-0 group-hover:scale-110 transition-transform" />
            <span className="text-xs text-slate-300 group-hover:text-slate-100">{s.text}</span>
            <ChevronRight className="w-3 h-3 text-slate-600 ml-auto group-hover:text-lime-400" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ==================== CHAT BUBBLE ====================
function ChatBubble({ message, onQuickAction }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="bg-lime-500/10 border border-lime-500/20 rounded-2xl rounded-tr-none px-4 py-3 max-w-[80%]">
          <p className="text-sm text-slate-200 whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  // AI Message
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-lime-500/20 flex items-center justify-center shrink-0 mt-0.5">
        <Bot className="w-4 h-4 text-lime-400" />
      </div>
      <div className="flex-1 space-y-3 max-w-[90%]">
        {/* Main answer */}
        <div className="bg-[#1e293b] border border-[#334155] rounded-2xl rounded-tl-none px-4 py-3">
          <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{message.content}</p>
        </div>

        {/* Key Metrics */}
        {message.metrics && message.metrics.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {message.metrics.map((m, i) => (
              <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2 flex items-center gap-2">
                {m.trend === "up" && <TrendingUp className="w-3.5 h-3.5 text-lime-400" />}
                {m.trend === "down" && <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
                {m.trend === "stable" && <Minus className="w-3.5 h-3.5 text-slate-500" />}
                <div>
                  <p className="text-[10px] text-slate-500">{m.label}</p>
                  <p className="text-xs font-semibold text-slate-200">{m.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recommendations */}
        {message.recommendations && message.recommendations.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Lightbulb className="w-3 h-3" /> Đề xuất hành động
            </p>
            {message.recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-2 bg-[#0f172a] border border-[#334155] rounded-lg px-3 py-2">
                <Badge className={`text-[9px] border-0 shrink-0 mt-0.5 ${
                  r.priority === "high" ? "bg-red-500/20 text-red-400" :
                  r.priority === "medium" ? "bg-amber-500/20 text-amber-400" :
                  "bg-sky-500/20 text-sky-400"
                }`}>{r.priority === "high" ? "Cao" : r.priority === "medium" ? "TB" : "Thấp"}</Badge>
                <div>
                  <p className="text-xs text-slate-200 font-medium">{r.title}</p>
                  {r.description && <p className="text-[10px] text-slate-500 mt-0.5">{r.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Drafts Created */}
        {message.action_drafts && message.action_drafts.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <ClipboardList className="w-3 h-3" /> Draft đã tạo
            </p>
            {message.action_drafts.map((d, i) => (
              <div key={i} className="flex items-center gap-2 bg-lime-500/5 border border-lime-500/20 rounded-lg px-3 py-2">
                <Badge className="bg-lime-500/20 text-lime-400 text-[9px] border-0">{d.type === "campaign" ? "Chiến dịch" : "Task"}</Badge>
                <span className="text-xs text-slate-200">{d.title}</span>
                <Badge className="bg-slate-500/20 text-slate-400 text-[9px] border-0 ml-auto">Draft</Badge>
              </div>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {message.quick_actions && message.quick_actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {message.quick_actions.map((a, i) => {
              const Icon = ACTION_ICONS[a.action_type] || Zap;
              return (
                <button key={i} onClick={() => onQuickAction(a)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1e293b] border border-[#334155] rounded-lg text-xs text-slate-300 hover:border-lime-500/30 hover:text-lime-400 transition-colors"
                  data-testid={`action-${a.action_type}-${i}`}>
                  <Icon className="w-3 h-3" /> {a.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Analysis summary - findings */}
        {message.analysis_summary && message.analysis_summary.findings && message.analysis_summary.findings.length > 0 && (
          <details className="group">
            <summary className="text-[10px] text-slate-600 cursor-pointer hover:text-slate-400 flex items-center gap-1">
              <BarChart3 className="w-3 h-3" /> Xem chi tiết phân tích ({message.analysis_summary.findings.length} phát hiện)
            </summary>
            <div className="mt-1.5 pl-4 border-l border-[#334155] space-y-1">
              {message.analysis_summary.findings.map((f, i) => (
                <p key={i} className="text-[10px] text-slate-500">• {f}</p>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}


// ==================== FEATURE SUGGESTIONS PANEL ====================
function FeatureSuggestionsPanel() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [prdDialog, setPrdDialog] = useState(null);
  const [prdLoading, setPrdLoading] = useState(false);
  const [prdResult, setPrdResult] = useState(null);

  const fetchSuggestions = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const endpoint = force ? "/copilot/feature-suggestions/refresh" : "/copilot/feature-suggestions";
      const method = force ? "post" : "get";
      const { data } = await api[method](endpoint);
      setSuggestions(data.suggestions || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi tải đề xuất"); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const updateStatus = async (idx, status) => {
    try {
      await api.put(`/copilot/feature-suggestions/${idx}/status`, { status });
      setSuggestions(prev => prev.map(s => s.index === idx ? { ...s, user_status: status } : s));
      toast.success(`Đã đánh dấu: ${STATUS_LABELS[status] || status}`);
    } catch { toast.error("Lỗi cập nhật"); }
  };

  const createPRD = async (suggestion) => {
    setPrdLoading(true);
    setPrdResult(null);
    try {
      const { data } = await api.post("/copilot/prd-drafts", {
        feature_name: suggestion.feature_name,
        problem_detected: suggestion.problem_detected,
        data_evidence: suggestion.data_evidence,
        expected_impact: suggestion.expected_impact,
        effort_level: suggestion.effort_level,
        priority_score: suggestion.priority_score,
        suggested_prd_summary: suggestion.suggested_prd_summary,
        source_suggestion_id: String(suggestion.index),
      });
      setPrdResult(data.full_prd || suggestion.suggested_prd_summary);
      toast.success("Đã tạo PRD draft");
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi tạo PRD"); }
    finally { setPrdLoading(false); }
  };

  const EFFORT_COLORS = {
    low: "bg-lime-500/20 text-lime-400",
    medium: "bg-amber-500/20 text-amber-400",
    high: "bg-red-500/20 text-red-400",
  };
  const EFFORT_LABELS = { low: "Thấp", medium: "TB", high: "Cao" };
  const PHASE_COLORS = {
    now: "bg-red-500/20 text-red-400",
    next: "bg-amber-500/20 text-amber-400",
    later: "bg-sky-500/20 text-sky-400",
  };
  const PHASE_LABELS = { now: "Ngay", next: "Tiếp theo", later: "Sau" };
  const STATUS_LABELS = { accepted: "Chấp nhận", rejected: "Từ chối", planned: "Lên kế hoạch", built: "Đã xây dựng" };
  const STATUS_COLORS = {
    accepted: "bg-lime-500/20 text-lime-400",
    rejected: "bg-red-500/20 text-red-400",
    planned: "bg-amber-500/20 text-amber-400",
    built: "bg-emerald-500/20 text-emerald-400",
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="features-panel">
      {/* Header */}
      <div className="sticky top-0 bg-[#0f172a] border-b border-[#1e293b] px-6 py-4 z-10">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div>
            <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-lime-400" /> Đề xuất tính năng
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">AI Product Strategist phân tích dữ liệu thực và đề xuất tính năng mới</p>
          </div>
          <Button onClick={() => fetchSuggestions(true)} disabled={refreshing}
            variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-lime-400 hover:border-lime-500/30 gap-1.5 text-xs"
            data-testid="refresh-suggestions-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Đang phân tích..." : "Làm mới"}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 max-w-5xl mx-auto space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-lime-400 animate-spin mb-3" />
            <p className="text-sm text-slate-500">AI đang phân tích dữ liệu kinh doanh...</p>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-16">
            <Rocket className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Chưa có đề xuất. Bấm "Làm mới" để AI phân tích.</p>
          </div>
        ) : (
          suggestions.map((s, i) => (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden hover:border-[#475569]/50 transition-colors"
              data-testid={`feature-card-${i}`}>
              {/* Card Header */}
              <div className="px-5 py-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-slate-200">{s.feature_name}</h3>
                      {s.user_status && (
                        <Badge className={`text-[9px] border-0 ${STATUS_COLORS[s.user_status] || ""}`}>
                          {STATUS_LABELS[s.user_status] || s.user_status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">{s.problem_detected}</p>
                  </div>
                  {/* Priority Score */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                      s.priority_score >= 80 ? "bg-red-500/15 text-red-400 border border-red-500/20" :
                      s.priority_score >= 60 ? "bg-amber-500/15 text-amber-400 border border-amber-500/20" :
                      "bg-sky-500/15 text-sky-400 border border-sky-500/20"
                    }`}>
                      {s.priority_score}
                    </div>
                    <span className="text-[9px] text-slate-600 mt-0.5">điểm</span>
                  </div>
                </div>

                {/* Evidence & Impact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                  <div className="bg-[#0f172a] rounded-lg px-3 py-2">
                    <p className="text-[9px] text-slate-600 uppercase tracking-wider mb-0.5">Bằng chứng</p>
                    <p className="text-[11px] text-slate-400">{s.data_evidence}</p>
                  </div>
                  <div className="bg-[#0f172a] rounded-lg px-3 py-2">
                    <p className="text-[9px] text-slate-600 uppercase tracking-wider mb-0.5">Tác động kỳ vọng</p>
                    <p className="text-[11px] text-lime-400">{s.expected_impact}</p>
                  </div>
                </div>

                {/* Tags & Summary */}
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`text-[9px] border-0 ${EFFORT_COLORS[s.effort_level] || ""}`}>
                    <Hammer className="w-2.5 h-2.5 mr-0.5" /> {EFFORT_LABELS[s.effort_level] || s.effort_level}
                  </Badge>
                  <Badge className={`text-[9px] border-0 ${PHASE_COLORS[s.recommended_phase] || ""}`}>
                    <Clock className="w-2.5 h-2.5 mr-0.5" /> {PHASE_LABELS[s.recommended_phase] || s.recommended_phase}
                  </Badge>
                </div>

                <p className="text-[11px] text-slate-500 italic">{s.suggested_prd_summary}</p>
              </div>

              {/* Card Actions */}
              <div className="flex items-center justify-between px-5 py-2.5 bg-[#0f172a]/50 border-t border-[#334155]/50">
                <div className="flex gap-1">
                  <button onClick={() => updateStatus(s.index, "accepted")}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors ${s.user_status === "accepted" ? "bg-lime-500/20 text-lime-400" : "text-slate-500 hover:text-lime-400"}`}
                    data-testid={`accept-${i}`}>
                    <Check className="w-3 h-3" /> Chấp nhận
                  </button>
                  <button onClick={() => updateStatus(s.index, "planned")}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors ${s.user_status === "planned" ? "bg-amber-500/20 text-amber-400" : "text-slate-500 hover:text-amber-400"}`}
                    data-testid={`plan-${i}`}>
                    <CalendarDays className="w-3 h-3" /> Lên kế hoạch
                  </button>
                  <button onClick={() => updateStatus(s.index, "rejected")}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors ${s.user_status === "rejected" ? "bg-red-500/20 text-red-400" : "text-slate-500 hover:text-red-400"}`}
                    data-testid={`reject-${i}`}>
                    <XCircle className="w-3 h-3" /> Từ chối
                  </button>
                </div>
                <Button onClick={() => { setPrdDialog(s); setPrdResult(null); }}
                  size="sm" className="bg-lime-500/10 text-lime-400 hover:bg-lime-500/20 border border-lime-500/20 h-7 text-[10px] gap-1"
                  data-testid={`create-prd-${i}`}>
                  <FileText className="w-3 h-3" /> Tạo PRD
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* PRD Dialog */}
      <Dialog open={!!prdDialog} onOpenChange={() => setPrdDialog(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-3xl max-h-[85vh] overflow-y-auto">
          {prdDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lime-400 flex items-center gap-2">
                  <FileText className="w-5 h-5" /> PRD: {prdDialog.feature_name}
                </DialogTitle>
              </DialogHeader>

              {!prdResult && !prdLoading && (
                <div className="space-y-3 mt-2">
                  <div className="bg-[#0f172a] rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase mb-1">Vấn đề</p>
                    <p className="text-xs text-slate-300">{prdDialog.problem_detected}</p>
                  </div>
                  <div className="bg-[#0f172a] rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase mb-1">Tóm tắt</p>
                    <p className="text-xs text-slate-300">{prdDialog.suggested_prd_summary}</p>
                  </div>
                  <Button onClick={() => createPRD(prdDialog)}
                    className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-2" data-testid="generate-prd-btn">
                    <Sparkles className="w-4 h-4" /> Tạo PRD chi tiết bằng AI
                  </Button>
                </div>
              )}

              {prdLoading && (
                <div className="flex flex-col items-center py-8">
                  <Loader2 className="w-6 h-6 text-lime-400 animate-spin mb-2" />
                  <p className="text-xs text-slate-500">AI đang soạn PRD...</p>
                </div>
              )}

              {prdResult && (
                <div className="mt-2 bg-[#0f172a] rounded-lg p-4 border border-[#334155]">
                  <pre className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed font-sans">{prdResult}</pre>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
